import { useState } from 'react';
import { Download, ChevronDown, FileText, File, FileCode } from 'lucide-react';
import { documentConverter, DocumentFormat } from '../services/documentConverter';
import { supabase } from '../lib/supabase';

interface DownloadButtonProps {
  content: string;
  documentType: string;
  documentTitle: string;
  documentTier: 'standard' | 'enhanced' | 'premium';
  documentGenerationId?: string;
  userId?: string;
  className?: string;
}

export function DownloadButton({
  content,
  documentType,
  documentTitle,
  documentTier,
  documentGenerationId,
  userId,
  className = '',
}: DownloadButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const formats: { format: DocumentFormat; label: string; icon: any }[] = [
    { format: 'pdf', label: 'PDF Document', icon: FileText },
    { format: 'docx', label: 'Word Document', icon: File },
    { format: 'txt', label: 'Plain Text', icon: FileText },
    { format: 'html', label: 'HTML File', icon: FileCode },
  ];

  const handleDownload = async (format: DocumentFormat) => {
    setIsDownloading(true);
    setIsOpen(false);

    try {
      await documentConverter.downloadDocument({
        documentContent: content,
        documentTitle,
        documentType,
        format,
      });

      if (documentGenerationId && userId) {
        await supabase.from('document_downloads').insert({
          document_generation_id: documentGenerationId,
          user_id: userId,
          file_format: format,
          document_tier: documentTier,
        });
      }

      if (window.parent !== window) {
        window.parent.postMessage(
          {
            type: 'documentDownloaded',
            source: 'tenancy-agreement-embed',
            timestamp: Date.now(),
            data: {
              documentGenerationId,
              format,
              documentTier,
            },
          },
          '*'
        );
      }
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download document. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        disabled={isDownloading}
        className="inline-flex items-center justify-center px-5 py-2.5 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
      >
        <Download className="h-4 w-4 mr-2" />
        {isDownloading ? 'Downloading...' : 'Download Now'}
        <ChevronDown className="h-4 w-4 ml-2" />
      </button>

      {isOpen && !isDownloading && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
            <div className="py-1" role="menu">
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">
                Select Format
              </div>
              {formats.map(({ format, label, icon: Icon }) => (
                <button
                  key={format}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDownload(format);
                  }}
                  className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors duration-150"
                  role="menuitem"
                >
                  <Icon className="h-4 w-4 mr-3 text-gray-400" />
                  <span className="flex-1">{label}</span>
                  <span className="text-xs text-gray-400 uppercase">{format}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
